<template>
  <div>
    <v-list-tile
      avatar
      class="store-tile"
      :class="{
        'no-hover-clr': !hoverable,
        'no-click-pointer': !hoverable,
        clickable: hoverable,
        [marginClass]: !!marginClass,
      }"
      :ripple="false"
      @click.stop="hoverable ? $emit('click', $event) : null"
    >
      <img
        v-if="showNewBadge"
        class="newbadge"
        :src="$store.state.common.newBadgeURL"
      >
      <v-list-tile-avatar>
        <v-icon
          large
          class="msr--text"
        >
          location_on
        </v-icon>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-sub-title>
          <small>{{ store.name }}</small>
        </v-list-tile-sub-title>
        <v-list-tile-sub-title>
          <v-layout
            v-if="allowEditAssociationMemberNumber"
            align-center
            style="min-height: 20px"
          >
            <small>
              {{
                store.association_membernumber ||
                  $t('storeAssociationMemberNumberNoneYet')
              }}
            </small>
            <v-btn
              small
              flat
              round
              style="
                min-width: 20px;
                max-width: 20px;
                min-height: 20px;
                max-height: 20px;
              "
              color="grey"
              class="ml-1 mr-0 my-0 hoverable-icon"
              :title="$t('storeAssociationMemberNumberEditTooltip')"
              @click.stop="openEditAssociationMemberNumberDialog()"
            >
              <v-icon style="font-size: 14px">
                edit
              </v-icon>
            </v-btn>
          </v-layout>
        </v-list-tile-sub-title>
        <v-list-tile-title
          :class="{
            'msr--text': highlightTitle,
            'grey--text text--darken-3': !highlightTitle,
          }"
        >
          <span style="font-size: 1.25em">
            <strong>{{ store.city }}</strong>
            <span
              v-if="
                store.countrycode && store.countrycode.toLowerCase() !== 'de'
              "
            >({{ store.countrycode.toUpperCase() }})</span>
            {{ store.location || '' }}
          </span>
          <div v-if="store.address">
            {{ store.address }}
          </div>
        </v-list-tile-title>
        <v-list-tile-sub-title>
          <v-icon
            v-if="showOfflineMarker"
            :title="$t('siteOffline')"
            class="error--text"
            style="margin-right: 0.25em"
            @click.stop="toggleOfflineDecodersDialog()"
          >
            error_outline
          </v-icon>
          <span
            v-if="
              numTvdecoders !== null &&
                (hideZeroDecoders ? numTvdecoders > 0 : true)
            "
            style="margin-right: 0.5em"
          >
            {{ numTvdecoders }} x
            <v-icon class="sub-icon">tv</v-icon>
          </span>
          <span
            v-if="
              numRadiodecoders !== null &&
                (hideZeroDecoders ? numRadiodecoders > 0 : true)
            "
            style="margin-right: 0.5em"
          >
            {{ numRadiodecoders }} x
            <v-icon class="sub-icon">radio</v-icon>
          </span>
          <span
            v-if="
              numTouchdecoders !== null &&
                (hideZeroDecoders ? numTouchdecoders > 0 : true)
            "
            style="margin-right: 0.5em"
          >
            {{ numTouchdecoders }} x
            <v-icon class="sub-icon">touch_app</v-icon>
          </span>
        </v-list-tile-sub-title>
        <v-list-tile-sub-title v-if="showDate">
          <div class="grey--text text--darken-1">
            {{ store.created_at | dayjs('DD.MM.YYYY') }}
          </div>
        </v-list-tile-sub-title>
      </v-list-tile-content>
      <v-list-tile-action>
        <slot name="actions">
          <v-icon
            medium
            :class="{flipped: expanded}"
            class="grey--text"
          >
            keyboard_arrow_down
          </v-icon>
        </slot>
      </v-list-tile-action>
    </v-list-tile>
    <!-- Offline Decoders Dialog -->
    <v-dialog
      v-model="showOfflineDecodersDialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :width="$store.state.common.largeDialogWidth"
      lazy
    >
      <v-card>
        <v-card-title class="pb-0 d-block">
          <v-layout align-center>
            <v-layout
              align-center
              wrap
            >
              <div class="headline mb-2">
                {{ $t('decodersSomeOffline') }}
              </div>
            </v-layout>
            <v-btn
              class="ml-auto mr-0 my-0"
              flat
              icon
              :title="$t('close')"
              @click="showOfflineDecodersDialog = false"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
        </v-card-title>
        <v-card-text>
          <p>
            {{ $t('offlineDecodersTroubleshootingHint') }}:
            <a
              :href="troubleshootingUrl"
              target="_blank"
            >Troubleshooting</a>
          </p>
          <v-list
            v-for="(decoder, decoderIndex) in sortedOfflineDecoders"
            :key="'offlineDecoder_' + decoderIndex"
            dense
            two-line
            class="pt-0 pb-0"
          >
            <v-list-tile
              :to="routePathForDecoder(decoder)"
              :class="{
                'more-space-for-sub-title':
                  decoder.slave_decoder_serials_and_descriptions.length,
              }"
            >
              <v-list-tile-avatar>
                <v-icon v-if="decoder.ctype === 'radio'">
                  radio
                </v-icon>
                <icon-tv-decoder
                  v-if="decoder.ctype === 'tv'"
                  :tvdecoder="decoder"
                />
                <v-icon v-if="decoder.ctype === 'touch'">
                  touch_app
                </v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{ decoder.description }}</v-list-tile-title>
                <v-list-tile-sub-title
                  :class="{
                    'more-space-for-sub-title':
                      decoder.slave_decoder_serials_and_descriptions.length,
                  }"
                >
                  {{ $t('serial') }}: {{ decoder.serial }}
                  <span
                    v-if="decoder.slave_decoder_serials_and_descriptions.length"
                  >
                    <br>
                    <span class="error--text">
                      {{ $t('affectedSlaveDecoderSerials') }}:
                      <b>{{
                        decoder.slave_decoder_serials_and_descriptions
                          .map((obj) => obj.description)
                          .join(', ')
                      }}</b>
                    </span>
                    <div style="min-height: 8px" />
                  </span>
                </v-list-tile-sub-title>
              </v-list-tile-content>
              <v-spacer />
              <v-icon
                v-if="!decoder.is_slave"
                medium
                class="grey--text clickable"
              >
                chevron_right
              </v-icon>
              <v-icon
                v-else
                medium
                class="grey--text text--lighten-2"
                :title="$t('isSlaveDecoder')"
              >
                link
              </v-icon>
            </v-list-tile>
            <v-divider v-if="decoderIndex < sortedOfflineDecoders.length - 1" />
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Edit Association Member Number Dialog -->
    <v-dialog
      v-model="showEditAssociationMemberNumberDialog"
      lazy
      persistent
      :width="600"
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-card>
        <v-card-title class="pb-0">
          <v-layout
            row
            align-center
            justify-center
          >
            <v-icon
              medium
              class="msr--text mr-3"
            >
              edit
            </v-icon>
            <h1 class="headline">
              {{ $t('storeAssociationMemberNumberEdit') }}
            </h1>
            <v-btn
              class="ml-auto mr-0 my-0"
              flat
              icon
              :title="$t('close')"
              @click="closeEditAssociationMemberNumberDialog()"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
        </v-card-title>
        <v-card-text>
          <div class="subheading">
            {{ $t('storeAssociationMemberNumberEditHint') }}
          </div>
          <div class="subheading msr--text mt-3">
            {{ $t('store') }}: {{ store.name }} ({{ store.city }})
          </div>
          <v-text-field
            ref="associationmembernumber"
            v-model="newAssociationMemberNumber"
            class="pt-0"
            :counter="associationMemberNumberMaxLength"
            :rules="[associationMemberNumberRules.maxLength]"
            :placeholder="$t('storeAssociationMemberNumberNoneYet')"
            @keyup.native.esc="closeEditAssociationMemberNumberDialog()"
            @keyup.native.enter="
              updateAssociationMemberNumber(store, newAssociationMemberNumber)
            "
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            round
            :disabled="!associationMemberNumberDirty"
            :loading="busy"
            @click="
              updateAssociationMemberNumber(store, newAssociationMemberNumber)
            "
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div
      v-if="!showDivider"
      :class="[dividerMarginClass]"
    />
    <v-divider
      v-if="showDivider"
      :class="[dividerMarginClass]"
    />
  </div>
</template>

<script>
import orderBy from 'lodash.orderby';

import BtnOk from '@/components/ButtonOk';
import IconTvDecoder from '@/components/IconTvDecoder';

export default {
  components: {
    BtnOk,
    IconTvDecoder,
  },
  props: {
    store: {
      type: Object,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    hoverable: {
      type: Boolean,
      default: true,
    },
    marginClass: {
      type: String,
      default: 'mt-1 mb-1',
    },
    showNewBadge: {
      type: Boolean,
      default: false,
    },
    showDate: {
      type: Boolean,
      default: false,
    },
    showDivider: {
      type: Boolean,
      default: true,
    },
    dividerMarginClass: {
      type: String,
      default: 'mt-2',
    },
    numTvdecoders: {
      type: Number,
      default: null,
    },
    numRadiodecoders: {
      type: Number,
      default: null,
    },
    numTouchdecoders: {
      type: Number,
      default: null,
    },
    hideZeroDecoders: {
      type: Boolean,
      default: true,
    },
    highlightTitle: {
      type: Boolean,
      default: false,
    },
    showOfflineMarker: {
      type: Boolean,
      default: false,
    },
    offlineDecoders: {
      type: Array[Object],
      default: () => [],
    },
    allowEditAssociationMemberNumber: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const memberNumberMaxLength = 64;
    return {
      busy: false,
      showOfflineDecodersDialog: false,
      showEditAssociationMemberNumberDialog: false,

      newAssociationMemberNumber: '',
      associationMemberNumberMaxLength: memberNumberMaxLength,
      associationMemberNumberRules: {
        maxLength: (v) => {
          return v.length <= memberNumberMaxLength || this.$t('tooLong');
        },
      },
    };
  },
  computed: {
    associationMemberNumberDirty() {
      return (
        this.store.association_membernumber !==
        this.newAssociationMemberNumber.trim()
      );
    },
    troubleshootingUrl() {
      return this.$store.state.common.troubleshootingUrl;
    },
    sortedOfflineDecoders() {
      return orderBy(this.offlineDecoders, 'description');
    },
  },
  methods: {
    openEditAssociationMemberNumberDialog() {
      this.newAssociationMemberNumber = this.store.association_membernumber;
      this.showEditAssociationMemberNumberDialog = true;
      this.$nextTick(() => {
        this.$refs.associationmembernumber.focus();
      });
    },
    closeEditAssociationMemberNumberDialog() {
      this.showEditAssociationMemberNumberDialog = false;
    },
    updateAssociationMemberNumber(store, newAssociationMemberNumber) {
      newAssociationMemberNumber = newAssociationMemberNumber.trim();

      this.busy = true;
      this.$api
        .updateStoreAssociationMemberNumber(
          store.id,
          newAssociationMemberNumber
        )
        .then((response) => {
          this.$bus.$emit('alert-success', {
            message: 'genericEntryUpdateSuccess',
            translate: true,
          });

          this.$store.commit(
            'SET_STORE_ASSOCIATION_MEMBERNUMBER_IN_SITES_LIST',
            {
              storeId: store.id,
              associationMemberNumber: newAssociationMemberNumber,
            }
          );
          this.showEditAssociationMemberNumberDialog = false;
        })
        .finally(() => {
          this.busy = false;
        });
    },
    toggleOfflineDecodersDialog() {
      this.showOfflineDecodersDialog = !this.showOfflineDecodersDialog;
    },
    routePathForDecoder(decoder) {
      if (decoder.is_slave) {
        return null;
      }
      return '/player/' + decoder.ctype + '/' + decoder.id;
    },
  },
};
</script>

<style scoped>
.more-space-for-sub-title {
  white-space: normal;
}
.more-space-for-sub-title >>> .v-list__tile.v-list__tile--link {
  height: auto;
}

.toolbar-light-bg {
  background-color: #f5f5f5;
}
</style>



// WEBPACK FOOTER //
// src/components/StoreTile.vue